import { useState, useEffect } from 'react';

/**
 * Scryfall API integration
 * Based on the first attempt HTML implementation
 */

export interface ScryfallCard {
  id: string;
  name: string;
  mana_cost: string;
  cmc: number;
  type_line: string;
  colors: string[];
  color_identity: string[];
  oracle_text?: string;
  rarity: string;
  legalities?: Record<string, string>;
  layout?: string;  // Card layout: 'normal', 'transform', 'modal_dfc', 'flip', etc.
  keywords?: string[];  // Array of keywords like "Partner", "Choose a Background", etc.
  set_name?: string;    // Full set name (e.g., "Double Masters")
  set?: string;         // Set code (e.g., "2xm")
  collector_number?: string;
  prices?: {
    usd?: string | null;
    usd_foil?: string | null;
    usd_etched?: string | null;
    eur?: string | null;
    eur_foil?: string | null;
    tix?: string | null;
  };
  purchase_uris?: {
    tcgplayer?: string;
    cardmarket?: string;
    cardhoarder?: string;
  };
  scryfall_uri?: string;
  image_uris?: {
    small: string;
    normal: string;
    large: string;
    png: string;
  };
  card_faces?: Array<{
    name: string;
    mana_cost: string;
    type_line: string;
    cmc?: number;  // Back faces may have different CMC (e.g., MDFC lands = 0)
    colors?: string[];
    oracle_text?: string;
    image_uris?: {
      small: string;
      normal: string;
      large: string;
      png: string;
    };
  }>;
}

const SCRYFALL_API_BASE = 'https://api.scryfall.com';
const cache = new Map<string, ScryfallCard>();

/**
 * Rate limiting helper
 */
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 100; // 100ms between requests

async function rateLimitedFetch(url: string, init?: RequestInit): Promise<Response> {
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;

  if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
    await new Promise(resolve =>
      setTimeout(resolve, MIN_REQUEST_INTERVAL - timeSinceLastRequest)
    );
  }

  lastRequestTime = Date.now();
  return fetch(url, init);
}

/**
 * Search for a card by name (fuzzy or exact)
 */
export async function fetchCardData(
  name: string,
  mode: 'fuzzy' | 'exact' = 'fuzzy'
): Promise<ScryfallCard> {
  const cacheKey = `${mode}:${name.toLowerCase()}`;

  if (cache.has(cacheKey)) {
    return cache.get(cacheKey)!;
  }

  const param = mode === 'exact' ? 'exact' : 'fuzzy';
  const url = `${SCRYFALL_API_BASE}/cards/named?${param}=${encodeURIComponent(name)}`;

  try {
    const response = await rateLimitedFetch(url);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`Card "${name}" not found`);
      }
      throw new Error(`Scryfall API error: ${response.status}`);
    }

    const data: ScryfallCard = await response.json();
    cache.set(cacheKey, data);
    return data;
  } catch (error) {
    console.error('Scryfall fetch error:', error);
    throw error;
  }
}

/**
 * Full-text search using Scryfall search syntax (e.g. "bolt f:modern t:instant").
 * NOTE: /cards/named does NOT understand search syntax — use this whenever
 * filters like f:, t:, o:, mv:, id<= are part of the query.
 * Returns the first page of results (empty array when nothing matches).
 */
export async function searchCards(query: string, order: 'name' | 'edhrec' | 'cmc' | 'usd' | 'released' = 'name'): Promise<ScryfallCard[]> {
  const url = `${SCRYFALL_API_BASE}/cards/search?q=${encodeURIComponent(query)}&unique=cards&order=${order}`;
  const response = await rateLimitedFetch(url);

  if (response.status === 404) return []; // Scryfall returns 404 for "no results"
  if (!response.ok) {
    let details = '';
    try {
      const body = await response.json();
      details = body?.details || '';
    } catch { /* ignore */ }
    throw new Error(details || `Scryfall API error: ${response.status}`);
  }

  const data = await response.json();
  return (data.data || []) as ScryfallCard[];
}

/**
 * Get card image URL (handles double-faced cards)
 */
export function getCardImageUrl(card: ScryfallCard): string {
  if (card.image_uris?.normal) {
    return card.image_uris.normal;
  }

  if (card.card_faces?.[0]?.image_uris?.normal) {
    return card.card_faces[0].image_uris.normal;
  }

  return '';
}

/**
 * Parse decklist text into card names and quantities
 */
export function parseDecklistText(text: string): Array<{ name: string; quantity: number }> {
  const lines = text.split(/\r?\n/);
  const cards: Array<{ name: string; quantity: number }> = [];

  // Regex: matches "4 Lightning Bolt" or "4x Lightning Bolt" or "4 Lightning Bolt (SET) 123"
  const lineRegex = /^\s*(\d+)[xX]?\s+(.+?)(?:\s+\([^)]+\)\s+\d+)?\s*$/;

  for (const line of lines) {
    const trimmed = line.trim();

    // Skip empty lines and comments
    if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('//')) {
      continue;
    }

    const match = trimmed.match(lineRegex);
    if (match) {
      const quantity = parseInt(match[1], 10);
      const name = match[2].trim();

      if (name && quantity > 0) {
        cards.push({ name, quantity });
      }
    }
  }

  return cards;
}

/**
 * Batch fetch multiple cards
 */
export async function fetchMultipleCards(
  cards: Array<{ name: string; quantity: number }>
): Promise<Array<{ card: ScryfallCard; quantity: number }>> {
  const results: Array<{ card: ScryfallCard; quantity: number }> = [];

  for (const { name, quantity } of cards) {
    try {
      const card = await fetchCardData(name, 'exact');
      results.push({ card, quantity });
    } catch (error) {
      console.error(`Failed to fetch "${name}":`, error);
      // Try fuzzy search as fallback
      try {
        const card = await fetchCardData(name, 'fuzzy');
        results.push({ card, quantity });
      } catch (fuzzyError) {
        console.error(`Fuzzy search also failed for "${name}"`);
      }
    }
  }

  return results;
}

/**
 * Clear the cache (useful for testing)
 */
export function clearCache(): void {
  cache.clear();
}

/**
 * Fetch format-legal lands matching deck's color identity
 */
export async function fetchFormatLegalLands(
  format: string = 'standard',
  colorIdentity: string[] = ['W', 'U', 'B', 'R', 'G']
): Promise<ScryfallCard[]> {
  const normFormat = (format === 'Auto' || !format ? 'standard' : format).toLowerCase();
  const scryfallFormat = normFormat === 'edh' ? 'commander' : normFormat;
  const colors = colorIdentity.length > 0 ? colorIdentity.join('') : 'c';
  const query = `t:land f:${scryfallFormat} id<=${colors}`;
  const url = `${SCRYFALL_API_BASE}/cards/search?q=${encodeURIComponent(query)}`;

  try {
    const response = await rateLimitedFetch(url);
    if (!response.ok) return [];
    const data = await response.json();
    return data.data || [];
  } catch (err) {
    console.error('Failed to fetch format legal lands:', err);
    return [];
  }
}

// Static Scryfall CDN image cache for common cards to resolve instantly without network calls
const KNOWN_CDN_IMAGES: Record<string, string> = {
  'gingerbrute': 'https://cards.scryfall.io/normal/front/0/9/09a4578a-7dc6-4da3-93ee-913b10be5740.jpg',
  'lightning strike': 'https://cards.scryfall.io/normal/front/8/8/88b13bc0-da54-4c3b-917c-7c8345a329f5.jpg',
  'llanowar elves': 'https://cards.scryfall.io/normal/front/6/a/6a0b230b-d391-4998-a3f7-7b158a0ec2cd.jpg',
  'fatal push': 'https://cards.scryfall.io/normal/front/6/e/6e9da54f-e660-466d-88b9-12349e5d4a1b.jpg',
  'thoughtseize': 'https://cards.scryfall.io/normal/front/b/2/b281a308-ab6b-47b6-bea7-42def2d90d59.jpg',
  'opt': 'https://cards.scryfall.io/normal/front/3/2/323ed101-2e2d-4518-a681-309191d8e12c.jpg',
  'forest': 'https://cards.scryfall.io/normal/front/1/7/17b2b629-87a3-41a4-9d54-20d099951b14.jpg',
  'mountain': 'https://cards.scryfall.io/normal/front/a/e/ae5617a2-f851-4e78-9580-f00e998782a2.jpg',
  'swamp': 'https://cards.scryfall.io/normal/front/9/9/99a4c8f2-d6ff-4ecf-9154-e0327f274fb8.jpg',
  'island': 'https://cards.scryfall.io/normal/front/f/f/ff3b34f6-80f4-4e3e-bc54-0354ed1a6977.jpg',
  'plains': 'https://cards.scryfall.io/normal/front/7/8/786a51d4-8395-4674-8848-0d12e69faaa8.jpg',
};

const urlCache = new Map<string, string>();

/**
 * Asynchronously resolve direct cards.scryfall.io CDN image URL for a card by name
 */
export async function resolveScryfallImageUrl(name: string): Promise<string> {
  if (!name) return '';
  const cleanName = name.includes(' // ') ? name.split(' // ')[0] : name;
  const key = cleanName.toLowerCase().trim();
  if (KNOWN_CDN_IMAGES[key]) return KNOWN_CDN_IMAGES[key];
  if (urlCache.has(key)) return urlCache.get(key)!;

  try {
    const cardData = await fetchCardData(cleanName, 'fuzzy');
    const imgUrl = getCardImageUrl(cardData);
    if (imgUrl) {
      urlCache.set(key, imgUrl);
      return imgUrl;
    }
  } catch (err) {
    console.warn(`Could not resolve image URL for ${cleanName}:`, err);
  }

  // Fallback to Scryfall named image proxy endpoint if API fetch fails or errors
  const fallbackUrl = `https://api.scryfall.com/cards/named?exact=${encodeURIComponent(cleanName)}&format=image`;
  urlCache.set(key, fallbackUrl);
  return fallbackUrl;
}

/**
 * React hook to resolve direct cards.scryfall.io image URL
 */
export function useResolvedCardImage(name: string, initialUrl?: string): string {
  const cleanName = name ? (name.includes(' // ') ? name.split(' // ')[0] : name).toLowerCase().trim() : '';
  const initialIsCdn = Boolean(initialUrl && initialUrl.includes('cards.scryfall.io'));
  const knownCdn = KNOWN_CDN_IMAGES[cleanName] || (urlCache.has(cleanName) ? urlCache.get(cleanName) : '');

  const computeBest = (cardName: string, cdnCandidate?: string, initCandidate?: string): string => {
    if (cdnCandidate) return cdnCandidate;
    if (initCandidate && initCandidate.trim() !== '') return initCandidate;
    if (!cardName) return '';
    const clean = cardName.includes(' // ') ? cardName.split(' // ')[0] : cardName;
    return `https://api.scryfall.com/cards/named?exact=${encodeURIComponent(clean.trim())}&format=image`;
  };

  const initialBest = computeBest(name, knownCdn, initialUrl);
  const [url, setUrl] = useState<string>(initialBest);

  useEffect(() => {
    let isMounted = true;
    const currentKnown = KNOWN_CDN_IMAGES[cleanName] || (urlCache.has(cleanName) ? urlCache.get(cleanName) : '');
    const bestNow = computeBest(name, currentKnown, initialUrl);

    // Sync immediate URL when props update
    setUrl(bestNow);

    if (name && !currentKnown && !initialIsCdn) {
      resolveScryfallImageUrl(name).then(resolved => {
        if (isMounted && resolved) {
          setUrl(resolved);
        }
      });
    }

    return () => {
      isMounted = false;
    };
  }, [name, initialUrl, cleanName, initialIsCdn]);

  return url || initialBest;
}


